import { Directive, Input, Output, EventEmitter, inject } from '@angular/core';
import { HisNotificationService } from '../../core/service/notifications/his-notification.service';
import { HisUiService } from '../../core/service/base/his-ui.service';
import { HisOption } from '../model/his-option';
import { HisSearchSelectWidget } from '../model/his-widget-search-select';
import { HisSearchSelectionSetting } from '../model/his-search-selection-setting';

@Directive()
export class HisSearchSelectComponentBase {
    private notificationService = inject(HisNotificationService);
    private uiService = inject(HisUiService);


    // unique id for html
    @Input() uid: string;

    // widget is only passed to let the plam extract attributes like helptext from the search config file;
    // it will be undefined when the component is used standalone, outside the generic search.
    @Input() widget: HisSearchSelectWidget;

    // filter selected Options
    @Input() removeSelectedOptionsFromDropdown = false;

    // the name of the search entity
    @Input() searchEntity: string;
    @Input() searchConfigFile: string;

    // Here we can have options selected by autocomplete or subsearch; so both classes must be supported
    @Input() selectedOptions: any[] = [];
    @Input() selectedOptionsIdColumn: string = undefined;
    @Input() selectedOptionsLabelColumn: string = undefined;

    // label of the 'selectItems' button
    @Input() selectItemsButtonLabel: string = undefined;
    // label of the 'changeSelectedItems' button
    @Input() changeSelectedItemsButtonLabel: string = undefined;

    // the number of result rows per page
    @Input() resultRowsPerPage = 10;

    // Used to determine the relevant UI elements
    protected selectionSettingEnum = HisSearchSelectionSetting;
    @Input() selectionSetting: HisSearchSelectionSetting = HisSearchSelectionSetting.MultiSelect;

    // display autocomplete ?
    @Input() showAutocomplete = true;
    // minimum length of autocomplete input to trigger a search
    @Input() minLength = 1;
    // maximum number of suggestions to show in autocomplete (null -> show all)
    @Input() limit: number = null;
    // show icon to open dropdown?
    @Input() showDropdownIcon = true;
    // show null item in autocomplete select list?
    @Input() showNullItem = false;

    // search constraints passed by parent component, like { 'Person.surname': 'Wolf', 'Person.firstname': 'Ulrich' }
    @Input() presetInputFields = {};

    @Output() changed: EventEmitter<any[]> = new EventEmitter();

    @Input() showUnequalOption: boolean;

    // true if this is the first input element in the search mask
    @Input() isFirstInputElement = false;

    @Input() showTableFilter = true;

    /** Limit the number of selections displayed. Overflow will be combined to a single label. */
    // default to 10, similar to JSF
    @Input() maxDisplayedSelections = 10;
    protected showAllSelected = false;

    getHtmlFocusElementId(): string {
        return this.uid;
    }

    /**
     * Add one element selected by autocomplete.
     *
     * @param selectedElement the element to add
     */
    addSelectedElement( selectedElement: HisOption ) {
        if (selectedElement) {
            console.log( 'Element selected by autocomplete:', selectedElement );
            // In multiSelect mode we do not want to add an element twice; just tell the user that the element is already there.
            // In singleSelect mode we do not need that check because the data is substituted anyway.
            if ( this.selectionSetting === this.selectionSettingEnum.MultiSelect && this.isElementAlreadySelected( selectedElement.id ) ) {
                let message = this.uiService.translate( 'genericSearch.alreadySelected', selectedElement.label );
                this.notificationService.clear();
                this.notificationService.info( message, 'Info' );
                return;
            }
            
            // Add selectedElement to selectedOptions: In singleSelect mode the previous selection is substituted;
            // in multiSelect mode the spread-operator required to have model and view synchronized
            // (with push() the reset button wouldn't work correctly)
            this.selectedOptions = ( this.selectionSetting === this.selectionSettingEnum.SingleSelect ) ? [selectedElement] : [...this.selectedOptions, selectedElement];
            this.changed.emit( this.selectedOptions );
        }
    }

    private isElementAlreadySelected( id: string ): boolean {
        // console.log( 'id =', id, 'has type', typeof id );
        for ( let option of this.selectedOptions ) {
            let optionId: string | number = option.id;
            // TODO According to class HisOption the option.id should be of string type
            // But obviously the search result obtained from the REST service contains number ids...
            // console.log( 'optionId =', optionId, 'has type', typeof optionId );
            if ( id === optionId.toString() ) {
                return true;
            }
        }
        return false;
    }

    /**
     * Delete the given option from the list of selected search result rows.
     *
     * @param option the element to remove from the selection
     */
    deleteSelectedOption( option: any ) {
        for ( let i = 0; i < this.selectedOptions.length; i++ ) {
            if ( this.selectedOptions[i] === option ) {
                this.selectedOptions.splice( i, 1 );
                break;
            }
        }
        this.fireOnChange();
    }

    /**
     * Vom Auswahl-Popup ausgelöstes Event.
     *
     * @param selectedOptions Array der neu ausgewählten Elemente
     */
    selectedOptionsChange( selectedOptions: any[] ) {
        this.selectedOptions = selectedOptions;
        this.fireOnChange();
    }

    /**
     * Notify HisSearchFormWidgetComponent that the model needs to be updated.
     */
    private fireOnChange() {
        console.log( 'HisSearchSelectComponent: Emit change event:', this.selectedOptions );
        this.changed.emit( this.selectedOptions );

        // re-hide any future overflow after the button disappears
        if (this.selectedOptions.length <= this.maxDisplayedSelections) {
            this.showAllSelected = false;
        }
    }

    toggleShowAllSelected() {
        this.showAllSelected = !this.showAllSelected;
    }

}
